import 'cropperjs/dist/cropper.css';

import cn from 'classnames';
import { noop } from 'lodash';
import {
  type ForwardedRef,
  forwardRef,
  type MemoExoticComponent,
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactCropper, { type ReactCropperElement, type ReactCropperProps } from 'react-cropper';

import { useBoolState } from '@didaskill/ui/utils';
import { getImageMeta } from '@didaskill/utils/web';

import { CROPPER_VIEW_MODES, CROPPER_VIEW_MODES_MAPPING } from './Cropper.constants';
import * as styles from './Cropper.styles';

export type CropperProps = Omit<
  ReactCropperProps,
  'crop' | 'data' | 'viewMode' | 'value' | 'onChange'
> & {
  /** Field has warning. */
  hasWarning?: boolean;
  /** Field has error. */
  hasError?: boolean;
  /** Cropper data. Positions. */
  value?: ReactCropperProps['data'];
  /** Called with { dataUrl, detail: { x, y, height, width, rotate, scaleX, scaleY } }. */
  onChange?: (data: Parameters<ReactCropperProps['crop']>[0]['detail']) => void;
  /** View mode. https://github.com/fengyuanchen/cropperjs#viewmode. */
  viewMode?: CROPPER_VIEW_MODES;
  /** Try to fit the height of the canvas to the height of the image. */
  fitHeight?: boolean;
  /** Height limit if fitHeight is true. */
  maxHeight?: number;
};

export const CropperBase = forwardRef(
  (
    {
      value = null,
      onChange = noop,
      hasWarning = false,
      hasError = false,
      viewMode = CROPPER_VIEW_MODES.CANVAS_FILLS_CONTAINER,
      fitHeight = true,
      maxHeight = Infinity,
      ...props
    }: CropperProps,
    ref: ForwardedRef<ReactCropperElement>,
  ) => {
    const [targetContainerHeight, onChangeTargetContainerHeight] = useState<number>(undefined);
    const [isReady, onSetReady, onSetNotReady] = useBoolState(!fitHeight);
    const containerRef = useRef<HTMLDivElement>(null);

    const onCrop = useCallback(
      ({ detail }) => {
        onChange(detail);
      },
      [onChange],
    );

    const style = useMemo(
      () => ({
        ...props.style,
        height: props.style?.height ?? targetContainerHeight,
      }),
      [props.style, targetContainerHeight],
    );

    useLayoutEffect(() => {
      (async () => {
        if (props.src && fitHeight) {
          const { width: imgWidth, height: imgHeight } = await getImageMeta(props.src);

          const { width: containerWidth } = containerRef.current?.getBoundingClientRect() || {};

          onChangeTargetContainerHeight(
            Math.min(maxHeight, imgHeight * (containerWidth / imgWidth)),
          );

          onSetReady();
        } else {
          onSetNotReady();
        }
      })();
    }, [props.src, fitHeight, maxHeight, onSetReady, onSetNotReady]);

    return (
      <div ref={containerRef}>
        {
          // React-cropper needs the height when mounting.
          isReady && (
            <ReactCropper
              {...props}
              ref={ref}
              data={value}
              crop={onCrop}
              className={cn({
                hasWarning: !!hasWarning && !hasError,
                hasError,
              })}
              viewMode={CROPPER_VIEW_MODES_MAPPING[viewMode]}
              css={styles.cropper}
              style={style}
            />
          )
        }
      </div>
    );
  },
);

CropperBase.displayName = 'Cropper';

export const Cropper: MemoExoticComponent<typeof CropperBase> & {
  VIEW_MODES?: typeof CROPPER_VIEW_MODES;
} = memo(CropperBase);

Cropper.VIEW_MODES = CROPPER_VIEW_MODES;
